import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { API_BASE_URL, ENDPOINTS } from '../config/api';
import { LOGO_URL } from '../config/brand';
import {
  Field, Label, Input,
} from '@headlessui/react';
import {
  Gem,
  ArrowLeftRight,
  ShieldCheck,
  AlertTriangle,
} from 'lucide-react';

// ── Floating grid dots (identical to LoginPage) ───────────────────────────────
const GridDots = () => (
  <div className="absolute inset-0 grid grid-cols-10 gap-0 pointer-events-none overflow-hidden" aria-hidden="true">
    {Array.from({ length: 80 }).map((_, i) => (
      <div
        key={i}
        className="w-1 h-1 rounded-full bg-blue-500/10 mx-auto my-7 animate-pulse"
        style={{ animationDelay: `${(i * 0.13) % 4}s`, animationDuration: '4s' }}
      />
    ))}
  </div>
);

// ── Stat counter with animation (identical to LoginPage) ──────────────────────
function AnimatedStat({ target, label, prefix = '', suffix = '' }) {
  const [val, setVal] = useState(0);
  const ref = useRef(null);
  useEffect(() => {
    const obs = new IntersectionObserver(([e]) => {
      if (!e.isIntersecting) return;
      obs.disconnect();
      const num = parseFloat(target.replace(/[^0-9.]/g, ''));
      const dur = 1400;
      const start = performance.now();
      const tick = (now) => {
        const p = Math.min((now - start) / dur, 1);
        const ease = 1 - Math.pow(1 - p, 3);
        setVal(Math.round(num * ease * 10) / 10);
        if (p < 1) requestAnimationFrame(tick);
      };
      requestAnimationFrame(tick);
    }, { threshold: 0.3 });
    if (ref.current) obs.observe(ref.current);
    return () => obs.disconnect();
  }, [target]);
  return (
    <div ref={ref} className="space-y-1">
      <div className="text-xl sm:text-2xl font-extrabold text-white font-mono tracking-tight">
        {prefix}{val}{suffix}
      </div>
      <div className="text-[9px] font-mono text-zinc-500 uppercase tracking-widest">{label}</div>
    </div>
  );
}

// ── Password strength bar ─────────────────────────────────────────────────────
function PasswordStrength({ password }) {
  const score = !password ? 0
    : password.length < 6 ? 1
    : password.length < 8 ? 2
    : /[A-Z]/.test(password) && /[0-9]/.test(password) ? 4
    : 3;
  const labels = ['', 'Weak', 'Fair', 'Good', 'Strong'];
  const colors = ['', '#EF4444', '#F59E0B', '#3B82F6', '#22C55E'];
  if (!password) return null;
  return (
    <div className="flex items-center gap-2 mt-1.5">
      <div className="flex gap-1 flex-1">
        {[1, 2, 3, 4].map(i => (
          <div
            key={i}
            className="flex-1 h-1 rounded-full"
            style={{
              backgroundColor: i <= score ? colors[score] : 'rgba(255,255,255,0.05)',
              transition: 'background-color 0.3s'
            }}
          />
        ))}
      </div>
      <span className="text-[10px] font-mono" style={{ color: colors[score] }}>{labels[score]}</span>
    </div>
  );
}

// ── Error box ─────────────────────────────────────────────────────────────────
const ErrorBox = ({ children, shake }) => (
  <div className={`p-3 bg-red-500/10 border border-red-500/20 rounded-lg text-red-400 text-xs leading-relaxed ${shake ? 'animate-shake' : ''}`}>
    {children}
  </div>
);

const FEATURES = [
  { icon: Gem, title: 'Vault Registry', text: 'Real-time diamond inventory across all branches.' },
  { icon: ArrowLeftRight, title: 'Audit Logistics', text: 'Secure transit chains with recipient signatures.' },
  { icon: ShieldCheck, title: 'Role-Aware Access', text: 'Granular permissions for owners, accountants, sales.' },
];

export default function ResetPasswordPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token') || '';

  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [showPw, setShowPw] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [shake, setShake] = useState(false);
  const [done, setDone] = useState(false);

  const triggerError = (msg) => {
    setError(msg);
    setShake(true);
    setTimeout(() => setShake(false), 600);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (password !== confirm) { triggerError('Passwords do not match.'); return; }
    setError(''); setLoading(true);
    try {
      const resp = await fetch(`${API_BASE_URL}${ENDPOINTS.AUTH.RESET_PASSWORD}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token, new_password: password }),
      });
      const data = await resp.json().catch(() => ({}));
      if (!resp.ok) throw new Error(data.detail || 'Reset failed.');
      setDone(true);
      setTimeout(() => navigate('/login'), 2500);
    } catch (err) {
      triggerError(err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex bg-[#09090b] text-[#fafafa] font-sans overflow-x-hidden relative">
      
      {/* ── LEFT brand panel ── */}
      <aside className="relative hidden lg:flex flex-col justify-between w-[50%] xl:w-[52%] bg-[#09090b] p-12 xl:p-16 border-r border-white/5 overflow-hidden">
        {/* Ambient background glows */}
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_20%_15%,rgba(59,130,246,0.06),transparent_50%),radial-gradient(circle_at_80%_60%,rgba(168,85,247,0.01),transparent_50%)] pointer-events-none z-0" />
        <div className="absolute inset-0 bg-[linear-gradient(rgba(255,255,255,0.003)_1px,transparent_1px),linear-gradient(90deg,rgba(255,255,255,0.003)_1px,transparent_1px)] bg-[size:40px_40px] pointer-events-none z-0" />
        <GridDots />

        {/* Logo */}
        <div className="flex items-center gap-3.5 relative z-10">
          <img src={LOGO_URL} alt="NexCarat Logo" className="w-10 h-10 object-contain" />
          <span className="font-display font-bold text-2xl tracking-tight text-white">
            NexCarat
          </span>
        </div>

        {/* Hero copy */}
        <div className="max-w-lg space-y-10 relative z-10 my-auto">
          <div className="inline-flex items-center gap-2.5 px-3.5 py-1.5 rounded-full border border-blue-500/20 bg-blue-500/5 text-[11px] font-mono tracking-wider uppercase text-blue-400">
            <span className="w-2 h-2 rounded-full bg-blue-500 animate-pulse" />
            Enterprise Diamond Registry
          </div>

          <h1 className="font-display font-extrabold text-4xl xl:text-5xl tracking-tight leading-[1.1] bg-gradient-to-b from-white via-zinc-200 to-zinc-500 bg-clip-text text-transparent">
            Every stone, every branch.<br />
            <span className="text-blue-500 italic font-semibold">One truth.</span>
          </h1>

          <p className="text-base text-zinc-400 leading-relaxed">
            Track diamonds from purchase to sale across branches worldwide. Coordinate secure transfers, issue consignment memos, and monitor analytics.
          </p>

          {/* Feature list */}
          <ul className="space-y-5">
            {FEATURES.map((f, i) => {
              const Icon = f.icon;
              return (
                <li key={i} className="flex items-start gap-4 text-sm text-zinc-455 animate-fadeIn" style={{ animationDelay: `${i * 0.1}s` }}>
                  <div className="w-8 h-8 rounded-lg bg-blue-500/10 border border-blue-500/10 flex items-center justify-center mt-0.5 flex-shrink-0">
                    <Icon size={15} className="text-blue-400" />
                  </div>
                  <div>
                    <span className="font-semibold text-zinc-200 block mb-0.5 text-sm sm:text-base">{f.title}</span>
                    <span className="text-zinc-550 leading-relaxed text-xs sm:text-sm">{f.text}</span>
                  </div>
                </li>
              );
            })}
          </ul>

          {/* Stats strip */}
          <div className="grid grid-cols-3 gap-4 pt-8 border-t border-white/5">
            <AnimatedStat target="10" suffix="+" label="Organizations" />
            <AnimatedStat target="50" suffix="+" label="Active Users" />
            <AnimatedStat target="100" suffix="%" label="Secure Audits" />
          </div>
        </div>

        <div className="text-xs font-mono text-zinc-600 relative z-10">
          © 2026 NEXCARAT · SECURE WORKSPACE
        </div>
      </aside>

      {/* ── RIGHT form panel ── */}
      <main className="flex-1 flex items-center justify-center p-8 sm:p-16 md:p-20 bg-[#09090b] overflow-y-auto relative">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_80%_85%,rgba(59,130,246,0.03),transparent_50%)] pointer-events-none z-0" />
        
        <div className="w-full max-w-md space-y-10 relative z-10 py-10">
          {!token ? (
            <div className="text-center space-y-8">
              <div className="w-16 h-16 rounded-full bg-red-500/10 border border-red-500/20 flex items-center justify-center mx-auto text-red-400 text-2xl"><AlertTriangle size={26} /></div>
              <div className="space-y-3">
                <h3 className="font-display font-extrabold text-2xl text-white tracking-tight">Invalid Link</h3>
                <p className="text-sm text-zinc-400">This password reset link is missing a valid token or has expired. Please request a new one.</p>
              </div>
              <button
                onClick={() => navigate('/login')}
                className="w-full py-3 rounded-lg border border-white/5 bg-zinc-900/50 hover:bg-zinc-800 text-zinc-300 text-sm font-semibold transition-all cursor-pointer"
              >
                ← Back to sign in
              </button>
            </div>
          ) : done ? (
            <div className="text-center space-y-8">
              <div className="w-16 h-16 rounded-full bg-green-500/10 border border-green-500/20 flex items-center justify-center mx-auto text-green-400 text-2xl">✓</div>
              <div className="space-y-3">
                <h3 className="font-display font-extrabold text-2xl text-white tracking-tight">Password Updated</h3>
                <p className="text-sm text-zinc-400">Your password was changed successfully. Redirecting you to the sign-in page...</p>
              </div>
            </div>
          ) : (
            <div className="space-y-8">
              <button
                onClick={() => navigate('/login')}
                className="flex items-center gap-2 text-sm font-mono text-zinc-400 hover:text-white transition-colors bg-transparent border-0 p-0 cursor-pointer"
              >
                ← Back to sign in
              </button>
              <div className="space-y-3">
                <h2 className="font-display font-extrabold text-3xl text-white tracking-tight">Set new password</h2>
                <p className="text-sm text-zinc-400">Choose a secure, strong password for your account.</p>
              </div>

              <form onSubmit={handleSubmit} className="space-y-6">
                <Field className="space-y-2 text-left">
                  <Label className="text-xs font-mono font-semibold tracking-wider uppercase text-zinc-400">New password</Label>
                  <div className="relative">
                    <Input
                      as="input"
                      className="w-full px-4 py-3 pr-12 bg-zinc-900/50 border border-white/5 focus:border-blue-500/50 rounded-lg text-sm text-white placeholder-zinc-600 focus:outline-none focus:ring-1 focus:ring-blue-500/50 transition-all font-sans"
                      type={showPw ? 'text' : 'password'}
                      placeholder="Min 8 characters"
                      value={password}
                      onChange={e => setPassword(e.target.value)}
                      required
                      minLength={8}
                      autoFocus
                    />
                    <button
                      type="button"
                      onClick={() => setShowPw(!showPw)}
                      className="absolute right-3.5 top-1/2 -translate-y-1/2 text-xs font-mono font-bold tracking-wider text-zinc-500 hover:text-white transition-colors bg-transparent border-0 cursor-pointer"
                    >
                      {showPw ? 'HIDE' : 'SHOW'}
                    </button>
                  </div>
                  <PasswordStrength password={password} />
                </Field>

                <Field className="space-y-2 text-left">
                  <Label className="text-xs font-mono font-semibold tracking-wider uppercase text-zinc-400">Confirm password</Label>
                  <Input
                    as="input"
                    className="w-full px-4 py-3 bg-zinc-900/50 border border-white/5 focus:border-blue-500/50 rounded-lg text-sm text-white placeholder-zinc-650 focus:outline-none focus:ring-1 focus:ring-blue-500/50 transition-all font-sans"
                    type={showPw ? 'text' : 'password'}
                    placeholder="Repeat password"
                    value={confirm}
                    onChange={e => setConfirm(e.target.value)}
                    required
                    minLength={8}
                  />
                </Field>

                {error && <ErrorBox shake={shake}>{error}</ErrorBox>}

                <button
                  type="submit"
                  disabled={loading}
                  className="w-full py-3 rounded-lg bg-blue-500 hover:bg-blue-600 disabled:opacity-50 text-white text-sm font-semibold shadow-[0_4px_16px_rgba(59,130,246,0.25)] hover:-translate-y-0.5 transition-all flex items-center justify-center gap-2 cursor-pointer border-0"
                >
                  {loading ? 'Saving password…' : 'Set new password →'}
                </button>
              </form>
            </div>
          )}
        </div>
      </main>
    </div>
  );
}
